import React from 'react';
import { useAPIContext } from './api';
import { WidgetAPI } from '../../../constructs/widget/interface';
import { ClientWidget } from '../../../types/client-widget';
import { WidgetSettings } from '../../../types/widget-settings';

export type CreateWidgetDataType = Pick<WidgetSettings, 'type' | 'title' | 'handle'>;

interface WidgetContextType {
  selectedWidgetId: string;
  setSelectedWidgetId: (widgetId: string) => void;
  widgets: ClientWidget[];
  isLoading: boolean;

  createWidget: (widgetData: CreateWidgetDataType) => Promise<ClientWidget>;
  updateWidget: (widgetId: string, updated: Partial<WidgetSettings>) => void;
}

const WidgetContext = React.createContext<WidgetContextType | undefined>(undefined);

const LAST_WIDGET_KEY = 'drupi_last_widget_id';

export const WidgetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { callEndpoint } = useAPIContext();
  const [selectedWidgetId, setSelectedWidgetIdState] = React.useState<string>(
    localStorage.getItem(LAST_WIDGET_KEY) || ''
  );
  const [widgets, setWidgets] = React.useState<ClientWidget[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const listWidgets = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await callEndpoint<WidgetAPI, 'list-widgets'>('widget', '', 'GET');
      if (response) {
        setWidgets(response);
      }
    } catch (err) {
      console.error('API Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [callEndpoint]);

  const createWidget = React.useCallback(
    async (widgetData: CreateWidgetDataType) => {
      try {
        const response = await callEndpoint<WidgetAPI, 'create-widget'>('widget', '', 'POST', widgetData);

        await listWidgets();

        if (response?.widgetId) {
          setSelectedWidgetId(response.widgetId);
        }

        return response;
      } catch (err) {
        console.error('Error creating widget:', err);
        throw err;
      }
    },
    [callEndpoint, listWidgets]
  );

  React.useEffect(() => {
    listWidgets();
  }, [listWidgets]);

  React.useEffect(() => {
    if (!selectedWidgetId && widgets.length > 0) {
      setSelectedWidgetId(widgets[0].widgetId);
    }
  }, [widgets, selectedWidgetId]);

  // Guardar en localStorage
  const setSelectedWidgetId = (widgetId: string) => {
    setSelectedWidgetIdState(widgetId);
    localStorage.setItem(LAST_WIDGET_KEY, widgetId);
  };

  const updateWidget = (widgetId: string, updated: Partial<WidgetSettings>) => {
    setWidgets(prev =>
      prev.map(widget =>
        widget.widgetId === widgetId ? { ...widget, settings: { ...widget.settings, ...updated } } : widget
      )
    );
  };

  return (
    <WidgetContext.Provider
      value={{
        selectedWidgetId,
        setSelectedWidgetId,
        widgets,
        isLoading,

        updateWidget,
        createWidget
      }}
    >
      {children}
    </WidgetContext.Provider>
  );
};

export const useWidgetContext = () => {
  const context = React.useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidgetContext must be used within WidgetProvider');
  }
  return context;
};
